import { createSelector } from 'reselect';
import pick from 'lodash/pick';
import { AllOrgsReduxState, OrgParamProps, OrganizationUserAsDropdownField } from './types';
import { COLUMNS_ORG_VALUES } from './constants';
import type { OrganizationEditFormContextType } from './contexts/OrganizationEditFormProvider';

const allOrganizationsSelector = (state: AllOrgsReduxState) => state.baus.allOrganizations;

const getAllOrganizationsList = (state: AllOrgsReduxState) =>
  allOrganizationsSelector(state).allOrganizationsList;

/**
 * getOrganizationUsers
 * Selects organizationUsers from the allOrganizations state.
 */
export const getOrganizationUsers = (state: AllOrgsReduxState, orgId: number) =>
  allOrganizationsSelector(state).organizationUsers[orgId];

/**
 * getHightouchAllotment
 * Selects hightouchAllotments from the allOrganizations state.
 */
export const getHightouchAllotment = (state: AllOrgsReduxState) =>
  allOrganizationsSelector(state).hightouchAllotments.allotment;

/**
 * selectAllOrganizationsList
 * Selects allOrganizationsList from the allOrganizations state.
 */
export const selectAllOrganizationsList = createSelector(
  [getAllOrganizationsList],
  (orgsList): Record<number, OrgParamProps> => {
    return orgsList.reduce(
      (organizationMap, org) => ({
        ...organizationMap,
        [org.id]: org,
      }),
      {},
    );
  },
);

/**
 * selectAllOrganizationsForCSVExportList
 * Selects the organization data formatted correctly for the "export to CSV" feature.
 */
export const selectAllOrganizationsForCSVExportList = createSelector(
  [getAllOrganizationsList],
  (orgsList) => {
    return orgsList.map((org) => pick(org, COLUMNS_ORG_VALUES));
  },
);

/**
 * selectOrganizationById
 * Selects a single organization from the organizations array by its id
 */
export const selectOrganizationById = createSelector(
  [selectAllOrganizationsList, (_: AllOrgsReduxState, id: number) => id],
  (orgsList, id) => orgsList[id],
);

/**
 * selectOrganizationAsFormData
 * Selects organization data to act as the default values for the organization edit form,
 * formatted to fit the react-hook form context.
 */
export const selectOrganizationAsFormData = createSelector(
  [selectOrganizationById],
  (org): OrganizationEditFormContextType => {
    return pick(org, [
      'adminUserId',
      'dataPolicy',
      'estimatedSize',
      'industry',
      'isEnabled',
      'isPayThroughApp',
      'name',
      'showLiveChat',
      'website',
    ]);
  },
);

/**
 * selectOrganizationUsersAsDropdownOptions
 * Selects organization users as dropdown options for the organization edit form.
 * This is used to populate the dropdown for selecting the organization admin.
 */
export const selectOrganizationUsersAsDropdownOptions = createSelector(
  getOrganizationUsers,
  (users = []): OrganizationUserAsDropdownField[] => {
    return users.map((user) => ({
      value: user.userId,
      label: user.userId,
      Suffix: user.fullName,
    }));
  },
);
